import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, Unlock, X, LogIn } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const INDUSTRIES = [
  "Consulting", "Retail & E-commerce", "Technology", "Real Estate", "Healthcare",
  "Hospitality", "Construction", "Finance", "Legal", "Education",
  "Marketing & Advertising", "Automotive", "Beauty & Wellness", "Fitness",
  "Food & Beverage", "Non-profit", "Entertainment & Events", "Manufacturing",
  "Transportation", "Other",
];

export default function AuthModal({ open, mode = "signup", onClose }) {
  const { register, login } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState(mode);
  const [form, setForm] = useState({
    business_name: "", industry: "Consulting", contact_name: "", email: "", phone: "", password: "",
  });
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => { setTab(mode); }, [mode, open]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.password) return;
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setSubmitting(true);
    try {
      await register(form);
      toast.success("Welcome to DONDRÁE! Your CRM Portal is now active.");
      onClose();
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignin = async (e) => {
    e.preventDefault();
    if (!creds.email || !creds.password) return;
    setSubmitting(true);
    try {
      const p = await login(creds.email, creds.password);
      toast.success("Signed in successfully.");
      onClose();
      navigate(p.role === "admin" ? "/admin/dashboard" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-xl p-4 overflow-y-auto">
      <div className="glass-panel bg-[#0a0a0b]/90 rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.5)] w-full max-w-2xl relative fade-in my-8 border border-white/10">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors z-20">
          <X size={18} />
        </button>
        <div className="p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />

          {/* Tabs */}
          <div className="flex justify-center mb-8 relative z-10">
            <div className="inline-flex bg-black/40 border border-white/10 rounded-xl p-1">
              <button onClick={() => setTab("signup")} className={`px-6 py-2 rounded-lg text-sm font-bold transition-colors ${tab === "signup" ? "bg-primary-600 text-white" : "text-zinc-400 hover:text-white"}`}>Sign Up</button>
              <button onClick={() => setTab("signin")} className={`px-6 py-2 rounded-lg text-sm font-bold transition-colors ${tab === "signin" ? "bg-primary-600 text-white" : "text-zinc-400 hover:text-white"}`}>Sign In</button>
            </div>
          </div>

          {tab === "signup" ? (
            <>
              <div className="text-center mb-8 relative z-10">
                <div className="w-16 h-16 bg-primary-900/50 text-primary-400 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-primary-500/30 shadow-[0_0_20px_rgba(59,130,246,0.3)]">
                  <Rocket size={26} />
                </div>
                <h2 className="text-3xl font-extrabold text-white tracking-tight font-display">Create your account</h2>
                <p className="text-zinc-400 mt-2 font-light text-sm max-w-md mx-auto">
                  Complete your Business Profile to unlock the platform and instantly activate your free CRM Portal.
                </p>
              </div>
              <form onSubmit={handleSignup} className="space-y-5 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Business Name *</label>
                    <input value={form.business_name} onChange={(e) => update("business_name", e.target.value)} required className="w-full px-5 py-3.5 rounded-xl dark-input" placeholder="e.g. Acme Corp" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Industry</label>
                    <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                      <SelectTrigger className="w-full px-5 py-6 rounded-xl dark-input border-white/10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10 text-white max-h-64 z-[300]">
                        {INDUSTRIES.map((i) => (<SelectItem key={i} value={i} className="focus:bg-white/10 focus:text-white">{i}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Your Full Name *</label>
                  <input value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} required className="w-full px-5 py-3.5 rounded-xl dark-input" placeholder="John Doe" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Email Address *</label>
                    <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="w-full px-5 py-3.5 rounded-xl dark-input" placeholder="john@example.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Password *</label>
                    <input type="password" value={form.password} onChange={(e) => update("password", e.target.value)} required className="w-full px-5 py-3.5 rounded-xl dark-input" placeholder="Min. 6 characters" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Phone Number</label>
                  <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="w-full px-5 py-3.5 rounded-xl dark-input" placeholder="(555) 000-0000" />
                </div>
                <div className="pt-3">
                  <button type="submit" disabled={submitting} className="w-full py-4 bg-white text-black rounded-xl font-bold hover:bg-zinc-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.2)] flex items-center justify-center text-lg disabled:opacity-60">
                    {submitting ? "Setting up…" : (<>Complete Setup &amp; Unlock Platform <Unlock size={20} className="ml-3" /></>)}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="text-center mb-8 relative z-10">
                <div className="w-16 h-16 bg-primary-900/50 text-primary-400 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-primary-500/30 shadow-[0_0_20px_rgba(59,130,246,0.3)]">
                  <LogIn size={26} />
                </div>
                <h2 className="text-3xl font-extrabold text-white tracking-tight font-display">Welcome back</h2>
                <p className="text-zinc-400 mt-2 font-light text-sm">Sign in to access your DONDRÁE workspace.</p>
              </div>
              <form onSubmit={handleSignin} className="space-y-5 relative z-10 max-w-md mx-auto">
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Email Address</label>
                  <input type="email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} required className="w-full px-5 py-3.5 rounded-xl dark-input" placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Password</label>
                  <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} required className="w-full px-5 py-3.5 rounded-xl dark-input" placeholder="Your password" />
                </div>
                <div className="pt-3">
                  <button type="submit" disabled={submitting} className="w-full py-4 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-500 transition-colors shadow-[0_0_20px_rgba(59,130,246,0.4)] border border-primary-400/50 flex items-center justify-center text-lg disabled:opacity-60">
                    {submitting ? "Signing in…" : (<>Sign In <LogIn size={18} className="ml-3" /></>)}
                  </button>
                </div>
                <p className="text-center text-xs text-zinc-500">Are you the platform admin? <a href="/admin" className="text-primary-400 hover:text-primary-300 font-bold">Admin login</a></p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
